import type { ReactNode } from 'react'
import { cva } from 'class-variance-authority'
import { cn } from '@/lib/utils.ts'

export interface SegmentedOption<TValue extends string = string> {
  value: TValue
  label: ReactNode
  disabled?: boolean
}

type SegmentedVariant = 'tab' | 'pill'
type SegmentedSize = 'sm' | 'md'

const wrapperStyles = cva('inline-flex', {
  variants: {
    variant: {
      tab: 'border border-gray-200 rounded-md overflow-hidden divide-x divide-gray-200',
      pill: 'gap-1 flex-wrap',
    },
    fullWidth: {
      true: 'w-full',
    },
  },
  defaultVariants: {
    variant: 'tab',
  },
})

const optionStyles = cva(
  'inline-flex items-center justify-center font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 disabled:opacity-50 disabled:cursor-not-allowed',
  {
    variants: {
      variant: {
        tab: 'flex-1 bg-white text-gray-600 hover:bg-gray-50 data-[active=true]:bg-brand-700 data-[active=true]:text-white data-[active=true]:hover:bg-brand-800',
        pill: 'rounded-md border data-[active=true]:bg-brand-700 data-[active=true]:border-brand-700 data-[active=true]:text-white bg-white border-gray-300 text-gray-700 hover:border-brand-500',
      },
      size: {
        sm: 'text-small px-2 py-1 gap-1',
        md: 'text-body px-3 py-2 gap-1.5',
      },
    },
    defaultVariants: {
      variant: 'tab',
      size: 'md',
    },
  },
)

interface SegmentedControlProps<TValue extends string = string> {
  value: TValue
  onValueChange: (value: TValue) => void
  options: SegmentedOption<TValue>[]
  variant?: SegmentedVariant
  size?: SegmentedSize
  fullWidth?: boolean
  className?: string
  'aria-label'?: string
}

/**
 * Gegenseitig ausschliessende Optionen mit visueller Tab-/Pill-Anmutung.
 * `variant="tab"` = fuellt die Breite (z.B. Login-Mode-Umschaltung).
 * `variant="pill"` = nebeneinander als lose Chips (z.B. Slot-Picker).
 */
export function SegmentedControl<TValue extends string = string>({
  value,
  onValueChange,
  options,
  variant = 'tab',
  size = 'md',
  fullWidth,
  className,
  'aria-label': ariaLabel,
}: SegmentedControlProps<TValue>) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn(wrapperStyles({ variant, fullWidth }), className)}
    >
      {options.map((option) => {
        const active = option.value === value
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={active}
            data-active={active}
            disabled={option.disabled}
            onClick={() => onValueChange(option.value)}
            className={optionStyles({ variant, size })}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}
